import React from 'react'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '../ui/button'
import { TrashIcon, XMarkIcon } from '@heroicons/react/16/solid'
import { Separator } from '../ui/separator'
import Field from '../ui/Field'
import { Label } from '../ui/Label'
import { Input } from '../ui/Input'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import useGetVaults from '@/services/api/vault/useGetVaults'
import _ from 'lodash'
import * as yup from 'yup'
import { useController, useForm, useWatch } from 'react-hook-form'
import FieldStatusMessage from '../ui/FieldStatusMessage'
import { yupResolver } from '@hookform/resolvers/yup'
import SimpleComboBox from '../SimpleComboBox'
import DatePicker from '../ui/DatePicker'
import useCreateMoneyPath from '@/services/api/money-path/useCreateMoneyPath'
import { formatDate } from 'date-fns'
import useGetMoneyPath from '@/services/api/money-path/useGetMoneyPath'
import useUpdateMoneyPath from '@/services/api/money-path/useUpdateMoneyPath'
import { Switch } from '../ui/switch'
import useGetMoneyPaths from '@/services/api/money-path/useGetMoneyPaths'

const schema = yup.object().shape({
  id: yup.number().nullable(),
  name: yup.string().required(),
  description: yup.string().nullable(),
  vaultId: yup.number().nonNullable().required(),
  percentage: yup.number().required().nonNullable(),
})

export default function MoneyPathFormModal() {
  const navigate = useNavigate()
  const params = useParams()
  const newForm = !params?.id
  const ruleId = params?.id
  const location = useLocation

  const { data: _vaults, status: vaultStatus } = useGetVaults()

  const vaults = _.flatten(
    _vaults?.map((vault) => {
      const res = vault.is_group ? vault.vaults : [vault]
      return res.filter((x) => !x.is_archived)
    }),
  )

  const {
    data: selectedMoneyPathForEditing,
    refetch: refetchMoneyPath,
    status: moneyPathStatus,
  } = useGetMoneyPath({
    id: ruleId,
  })

  const { data: moneyPaths } = useGetMoneyPaths()

  const {
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors, defaultValues, isDirty },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: async () => {
      let selectedMoneyPathForEditing = null
      if (ruleId) {
        const { data: _selectedMoneyPathForEditing } = await refetchMoneyPath(
          {
            cancelRefetch: false,
          },
        )
        selectedMoneyPathForEditing = _selectedMoneyPathForEditing
      }

      return {
        id: params?.id,
        name: selectedMoneyPathForEditing?.name ?? null,
        description: selectedMoneyPathForEditing?.description ?? null,
        vaultId: selectedMoneyPathForEditing?.vault_id ?? null,
        percentage:
          selectedMoneyPathForEditing?.current_path_date?.percentage ?? 0,
      }
    },
  })

  const { action: create, status: createStatus } = useCreateMoneyPath({
    clientServerFieldMapping: {
      vaultId: 'vault_id',
    },
    setError,
  })

  const { action: update, status: updateStatus } = useUpdateMoneyPath({
    id: ruleId,
    clientServerFieldMapping: {
      vaultId: 'vault_id',
    },
    setError,
  })

  const { field: vaultIdField } = useController({
    control: control,
    name: 'vaultId',
    shouldUnregister: true,
  })

  const vaultId = useWatch({
    control,
    name: 'vaultId',
  })

  const handleClose = () => {
    if (location.state?.from === '/money-paths') {
      navigate('/money-paths')
    } else {
      navigate(-1) // Go back if possible
    }
  }

  async function submit(data) {
    const sumOfPercentage = moneyPaths.reduce(
      (total, item) =>
        total + (item.id == ruleId ? 0 : item.current_path_date.percentage),
      0,
    )

    if (sumOfPercentage + data.percentage > 100) {
      setError('percentage', {
        message: 'Total percentage allocation exceeds 100%',
      })
      return
    }

    const payload = {
      name: data.name,
      description: data.description,
      percentage: data.percentage,
      vaultId: data.vaultId,
    }

    // update
    if (ruleId) {
      await update({ ...payload })
    } else {
      await create(payload)
    }

    // navigate('/money-paths')
    handleClose()
  }

  const selectedVault = vaults.find((x) => x.id === vaultId)

  return (
    <Dialog open={true}>
      <DialogContent
        onSubmit={handleSubmit(submit)}
        as="form"
        className="md:max-w-[800px]---"
      >
        <DialogHeader>
          <DialogTitle>
            {ruleId ? 'Edit Money Path' : 'Create New Money Path'}
          </DialogTitle>
          <DialogClose onClick={handleClose}>
            <XMarkIcon />
          </DialogClose>
        </DialogHeader>
        <Separator className="" />
        <DialogBody className="">
          <div className="gap-4- flex flex-col gap-field-gap">
            <div>
              <Field
                status={errors.name?.message ? 'error' : undefined}
                statusText={errors.name?.message.toString()}
              >
                <Label>Name</Label>
                <Input {...register('name')} />
                <FieldStatusMessage />
              </Field>
            </div>

            <div>
              <Field
                status={errors.description?.message ? 'error' : undefined}
                statusText={errors.description?.message.toString()}
              >
                <Label>Description</Label>
                <Input {...register('description')} />
                <FieldStatusMessage />
              </Field>
            </div>

            <div>
              <Field
                status={errors.vaultId?.message ? 'error' : undefined}
                statusText={errors.vaultId?.message.toString()}
              >
                <Label>Vault</Label>
                <SimpleComboBox
                  align="start"
                  items={vaults}
                  selected={selectedVault}
                  setSelected={(item) => {
                    vaultIdField.onChange(item.id)
                  }}
                  getOptionLabel={(item) => (
                    <div>
                      <p>{item.name}</p>
                    </div>
                  )}
                  getSelectedLabel={(item) => item.name}
                  getValue={(item) => item.name}
                />
                <FieldStatusMessage />
              </Field>
            </div>

            <div>
              <Field
                status={errors.percentage?.message ? 'error' : undefined}
                statusText={errors.percentage?.message.toString()}
              >
                <Label>Percentage</Label>
                <Input
                  {...register('percentage')}
                  onBeforeInput={(e) => {
                    const input = (e.target as HTMLInputElement).value
                    const nextValue = input + e.data
                    const regex = /^\d*\.?\d{0,2}$/

                    if (!regex.test(nextValue)) {
                      e.preventDefault()
                    }
                  }}
                />
                <FieldStatusMessage />
              </Field>
            </div>
          </div>
        </DialogBody>
        <Separator className="" />
        <DialogFooter className="">
          <Button
            type="submit"
            loading={
              createStatus.status === 'pending' ||
              updateStatus.status === 'pending'
            }
          >
            Submit
          </Button>
          <Button onClick={handleClose} type="button" variant="secondary">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
